import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getCheckinLikesSummary } from "@/data/raw";
import { useAuth } from "@/hooks/useAuth";

export type LikeSummary = { count: number; liked: boolean };

interface Ctx {
  register: (id: string) => void;
  get: (id: string) => LikeSummary | undefined;
  mutate: (id: string, next: LikeSummary) => void;
  version: number; // bumps on any change so consumers re-render
}

const CheckinLikesContext = createContext<Ctx | null>(null);

const FLUSH_DELAY_MS = 40;
const MAX_RETRIES = 4;
const RETRY_BASE_MS = 600;

export function CheckinLikesProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  const mapRef = useRef<Map<string, LikeSummary>>(new Map());
  const pendingRef = useRef<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retriesRef = useRef(0);
  // Bumped whenever the viewer changes so in-flight responses from a previous
  // viewer (or a previous cache generation) can be discarded.
  const genRef = useRef(0);
  const [version, setVersion] = useState(0);

  // When the viewer changes, viewer_liked must be re-evaluated → reset cache
  // and re-queue every id we already know about so counts reappear.
  useEffect(() => {
    genRef.current += 1;
    retriesRef.current = 0;
    const knownIds = Array.from(mapRef.current.keys());
    mapRef.current = new Map();
    for (const id of knownIds) pendingRef.current.add(id);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pendingRef.current.size > 0) {
      timerRef.current = setTimeout(() => flushRef.current(), FLUSH_DELAY_MS);
    }
    setVersion((v) => v + 1);
  }, [userId]);

  const flush = useCallback(async () => {
    timerRef.current = null;
    const ids = Array.from(pendingRef.current);
    pendingRef.current = new Set();
    if (ids.length === 0) return;

    const gen = genRef.current;

    // Re-queue ids and retry with backoff. Without this a single failed
    // request (cold start / offline / token refresh) left every flame at 0
    // with no way to recover until a full app restart.
    const requeue = () => {
      if (gen !== genRef.current) return;
      for (const id of ids) {
        if (!mapRef.current.has(id)) pendingRef.current.add(id);
      }
      if (pendingRef.current.size === 0) return;
      if (retriesRef.current >= MAX_RETRIES) return;
      const delay = RETRY_BASE_MS * 2 ** retriesRef.current;
      retriesRef.current += 1;
      if (timerRef.current == null) {
        timerRef.current = setTimeout(() => flushRef.current(), delay);
      }
    };

    try {
      const { data, error } = await getCheckinLikesSummary(ids);
      if (gen !== genRef.current) return;
      if (error || !Array.isArray(data)) {
        requeue();
        return;
      }
      retriesRef.current = 0;
      for (const row of data as Array<{ checkin_id: string; like_count: number | string; viewer_liked: boolean }>) {
        mapRef.current.set(row.checkin_id, {
          count: Number(row.like_count ?? 0),
          liked: !!row.viewer_liked,
        });
      }
      // Backfill any ids the server didn't return (defensive — should not happen).
      for (const id of ids) {
        if (!mapRef.current.has(id)) {
          mapRef.current.set(id, { count: 0, liked: false });
        }
      }
      setVersion((v) => v + 1);
    } catch {
      requeue();
    }
  }, []);

  // Stable indirection so timers scheduled elsewhere always call the latest flush.
  const flushRef = useRef(flush);
  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  const register = useCallback((id: string) => {
    if (!id) return;
    if (mapRef.current.has(id) || pendingRef.current.has(id)) return;
    pendingRef.current.add(id);
    if (timerRef.current == null) {
      timerRef.current = setTimeout(() => flushRef.current(), FLUSH_DELAY_MS);
    }
  }, []);

  const get = useCallback((id: string) => mapRef.current.get(id), []);

  const mutate = useCallback((id: string, next: LikeSummary) => {
    mapRef.current.set(id, next);
    setVersion((v) => v + 1);
  }, []);

  // Refresh counts when the app returns to the foreground (native) or the tab
  // becomes visible again — likes that arrived while backgrounded now show up.
  useEffect(() => {
    let lastRun = 0;
    const refetchAll = () => {
      const now = Date.now();
      if (now - lastRun < 2000) return;
      lastRun = now;
      const knownIds = Array.from(mapRef.current.keys());
      if (knownIds.length === 0) return;
      retriesRef.current = 0;
      for (const id of knownIds) pendingRef.current.add(id);
      if (timerRef.current == null) {
        timerRef.current = setTimeout(() => flushRef.current(), FLUSH_DELAY_MS);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") refetchAll();
    };
    document.addEventListener("visibilitychange", onVisibility);

    let removeAppListener: (() => void) | undefined;
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) refetchAll();
        });
        removeAppListener = () => handle.remove();
      } catch {
        // Not running in Capacitor; visibility listener is enough.
      }
    })();

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      removeAppListener?.();
    };
  }, []);

  const value = useMemo<Ctx>(() => ({ register, get, mutate, version }), [register, get, mutate, version]);

  return <CheckinLikesContext.Provider value={value}>{children}</CheckinLikesContext.Provider>;
}

/**
 * Returns the batched like summary for a checkin id.
 * Returns `null` when no provider is mounted (caller should fall back to per-id fetch).
 */
export function useCheckinLikeSummary(checkinId: string | undefined | null) {
  const ctx = useContext(CheckinLikesContext);

  useEffect(() => {
    if (!ctx || !checkinId) return;
    ctx.register(checkinId);
  }, [ctx, checkinId]);

  if (!ctx) return null;
  const summary = checkinId ? ctx.get(checkinId) : undefined;
  return {
    summary, // undefined until first batch returns
    mutate: (next: LikeSummary) => {
      if (checkinId) ctx.mutate(checkinId, next);
    },
  };
}
